import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { STRIPE_ENABLED } from "./config/features";
import Landing from "./pages/Landing";
import BookDemo from "./pages/BookDemo";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";

// Everything behind login (plus the auth screens) lives in lazy chunks so the
// public marketing pages ship without Mantine or the app code.
const MantineRoot = lazy(() => import("./MantineRoot"));
const AppLayout = lazy(() =>
  import("./components/layout/AppLayout").then((m) => ({ default: m.AppLayout }))
);
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const VerifyEmailNotice = lazy(() => import("./pages/VerifyEmailNotice"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const OrganizationList = lazy(() => import("./pages/organizations/OrganizationList"));
const OrganizationDetail = lazy(() => import("./pages/organizations/OrganizationDetail"));
const UserList = lazy(() => import("./pages/users/UserList"));
const LocationList = lazy(() => import("./pages/locations/LocationList"));
const ClassTypeList = lazy(() => import("./pages/class-types/ClassTypeList"));
const MembershipPlanList = lazy(() => import("./pages/membership-plans/MembershipPlanList"));
const ProductList = lazy(() => import("./pages/products/ProductList"));
const SchedulePage = lazy(() => import("./pages/schedule/SchedulePage"));
const ClassDetail = lazy(() => import("./pages/schedule/ClassDetail"));
const BookingList = lazy(() => import("./pages/bookings/BookingList"));
const AttendanceList = lazy(() => import("./pages/attendance/AttendanceList"));
const AchievementsPage = lazy(() => import("./pages/achievements/AchievementList"));
const ProfilePage = lazy(() => import("./pages/profile/ProfilePage"));
const BillingPage = lazy(() => import("./pages/billing/BillingPage"));
const BrandingPage = lazy(() => import("./pages/branding/BrandingPage"));
const PaymentsPage = lazy(() => import("./pages/payments/PaymentsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Schedules and Sessions were merged into one Class Schedule page; keep old
// links and bookmarks working.
function LegacyClassRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/schedule/${id}` : "/schedule"} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#08090B" }} />}>
            <Routes>
              {/* Public marketing pages — no Mantine, load with the entry bundle */}
              <Route path="/" element={<Landing />} />
              <Route path="/book-demo" element={<BookDemo />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-mobile" element={<Privacy />} />

              {/* Auth + app routes, lazy-loaded inside the Mantine provider */}
              <Route element={<MantineRoot />}>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/set-password" element={<ResetPassword invite />} />
                <Route path="/verify-email" element={<VerifyEmail />} />

                {/* Protected without layout */}
                <Route
                  path="/verify-email-notice"
                  element={
                    <ProtectedRoute requireVerified={false}>
                      <VerifyEmailNotice />
                    </ProtectedRoute>
                  }
                />

                {/* Protected with AppLayout */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/organizations" element={<OrganizationList />} />
                  <Route path="/organizations/:id" element={<OrganizationDetail />} />
                  <Route path="/users" element={<UserList />} />
                  <Route path="/locations" element={<LocationList />} />
                  <Route path="/class-types" element={<ClassTypeList />} />
                  <Route path="/membership-plans" element={<MembershipPlanList />} />
                  <Route path="/products" element={<ProductList />} />
                  <Route path="/schedule" element={<SchedulePage />} />
                  <Route path="/schedule/:id" element={<ClassDetail />} />
                  <Route path="/class-schedules" element={<Navigate to="/schedule" replace />} />
                  <Route path="/class-sessions" element={<Navigate to="/schedule" replace />} />
                  <Route path="/class-sessions/:id" element={<LegacyClassRedirect />} />
                  <Route path="/bookings" element={<BookingList />} />
                  <Route path="/attendance" element={<AttendanceList />} />
                  <Route path="/achievements" element={<AchievementsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  {STRIPE_ENABLED && <Route path="/billing" element={<BillingPage />} />}
                  <Route path="/branding" element={<BrandingPage />} />
                  <Route path="/payments" element={<PaymentsPage />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
